import { pluginFactory } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';

import type { AiQuickPromptAction, AiQuickPromptState, EligibleRange } from './types';

export const pluginKey = new PluginKey<AiQuickPromptState>('aiQuickPromptPlugin');

const MAX_PREVIOUS_SHOWN_TEXTS = 1;
const MAX_PREVIOUS_TRIGGER_TEXTS = 1;

export const initialState: AiQuickPromptState = {
	isPromptVisible: false,
	eligibleRange: undefined,
	previousShownTexts: [],
	previousTriggerTexts: [],
};

export function reducer(
	state: AiQuickPromptState,
	action: AiQuickPromptAction,
): AiQuickPromptState {
	switch (action.type) {
		case 'SHOW_PROMPT': {
			if (!action.range) {
				return state;
			}

			let previousShownTexts = !!action.shownText?.trim()
				? [action.shownText.trim(), ...state.previousShownTexts]
				: state.previousShownTexts;
			if (previousShownTexts.length > MAX_PREVIOUS_SHOWN_TEXTS) {
				previousShownTexts = previousShownTexts.slice(0, MAX_PREVIOUS_SHOWN_TEXTS);
			}

			return {
				...state,
				isPromptVisible: true,
				eligibleRange: action.range,
				previousShownTexts,
			};
		}
		case 'HIDE_PROMPT': {
			if (!state.isPromptVisible) {
				return state;
			}

			let previousTriggerTexts = !!action.triggerText?.trim()
				? [action.triggerText.trim(), ...state.previousTriggerTexts]
				: state.previousTriggerTexts;
			if (previousTriggerTexts.length > MAX_PREVIOUS_TRIGGER_TEXTS) {
				previousTriggerTexts = previousTriggerTexts.slice(0, MAX_PREVIOUS_TRIGGER_TEXTS);
			}

			return {
				...state,
				isPromptVisible: false,
				eligibleRange: undefined,
				previousTriggerTexts,
			};
		}
		default:
			return state;
	}
}

export const { createPluginState, createCommand, getPluginState } = pluginFactory(
	pluginKey,
	reducer,
	{
		mapping: (tr, pluginState) => {
			let newState = pluginState;

			if (newState.eligibleRange) {
				const newFrom = tr.mapping.map(newState.eligibleRange.from);
				const newTo = tr.mapping.map(newState.eligibleRange.to);
				if (newFrom !== newState.eligibleRange.from || newTo !== newState.eligibleRange.to) {
					newState = {
						...newState,
						eligibleRange: { from: newFrom, to: newTo },
					};
				}
			}

			return newState;
		},

		// Hide the prompt when the selection changes
		// idleDetector will be stopped in the view update callback
		onSelectionChanged: (_tr, pluginState, editorState) => {
			let newState = pluginState;

			if (newState.isPromptVisible && newState.eligibleRange) {
				const { from, to } = newState.eligibleRange;
				const sel = editorState.selection;
				if (sel.from !== from || sel.to !== to) {
					newState = {
						...newState,
						isPromptVisible: false,
						eligibleRange: undefined,
					};
				}
			}

			return newState;
		},
	},
);

export const createShowPromptCommand = ({
	range,
	shownText,
}: {
	range: EligibleRange;
	shownText: string;
}) =>
	createCommand<AiQuickPromptAction>(() => {
		return {
			type: 'SHOW_PROMPT',
			range,
			shownText,
		};
	});

export const createHidePromptCommand = ({ triggerText }: { triggerText?: string } = {}) =>
	createCommand<AiQuickPromptAction>({
		type: 'HIDE_PROMPT',
		triggerText,
	});
